import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonDirective,
  FormControlDirective,
  FormFeedbackComponent,
  InputGroupComponent,
  InputGroupTextDirective
} from '@coreui/angular-pro';
import { IconDirective, IconModule, IconSetService } from '@coreui/icons-angular';
import { cilUser, cilEnvelopeClosed } from '@coreui/icons';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe } from 'i18n';
import { Logger, UnassistedInputDirective } from 'shared';

import { FunnelService } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'FAC3LESS Funnel';

@Component({
  selector: 'app-funnel-landing-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonDirective,
    FormControlDirective,
    FormFeedbackComponent,
    InputGroupComponent,
    InputGroupTextDirective,
    IconDirective,
    IconModule,
    UnassistedInputDirective,
    TranslatePipe,
    FunnelShellComponent
  ],
  templateUrl: './funnel-landing-page.component.html',
  styleUrls: ['./funnel-landing-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelLandingPageComponent {
  private fb         = inject(FormBuilder);
  private http       = inject(HttpClient);
  private router     = inject(Router);
  private route      = inject(ActivatedRoute);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);
  private iconSet    = inject(IconSetService);
  private logger     = new Logger('FunnelLandingPage');

  form!: FormGroup;

  captchaToken    = signal<string | null>(null);
  captchaError    = signal<boolean>(false);
  mcaptchaConfig  = signal<{ widgetUrl: string; sitekey: string } | null>(null);

  loading = signal<boolean>(false);
  error   = signal<string | null>(null);

  /** sponsorId from /r/:sponsorId — null or "0" means no sponsor. Persisted so the form survives reload. */
  sponsorId = signal<string | null>(null);

  firstNameCtrl = computed(() => this.form.get('first_name')!);
  emailCtrl     = computed(() => this.form.get('email')!);

  formStatus!: ReturnType<typeof toSignal<string>>;
  canSubmit!: ReturnType<typeof computed<boolean>>;

  constructor() {
    this.iconSet.icons = { ...this.iconSet.icons, cilUser, cilEnvelopeClosed };

    this.form = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name:  [''],
      email:      ['', [Validators.required, Validators.email]]
    });

    this.formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
    this.canSubmit  = computed(() =>
      this.formStatus() === 'VALID' && !!this.captchaToken() && !this.loading()
    );

    // Capture sponsor from route param and persist it, so re-entering the funnel
    // without the /r/:sponsorId path (reload elsewhere, back-nav, bookmark) keeps
    // the referrer instead of silently sending sponsor_id: null.
    const raw = this.route.snapshot.params['sponsorId'];
    if (raw && raw !== '0') {
      this.sponsorId.set(raw);
      try { sessionStorage.setItem('distribution.funnel.sponsor_id', raw); } catch { /* ignore */ }
    } else {
      let stored: string | null = null;
      try { stored = sessionStorage.getItem('distribution.funnel.sponsor_id'); } catch { /* ignore */ }
      this.sponsorId.set(stored);
    }

    effect(() => this.loadMcaptchaConfig());
    effect(() => { if (this.mcaptchaConfig()) this.loadMcaptchaWidget(); });

    window.addEventListener('message', this.handleMcaptchaMessage.bind(this));
  }

  // ---- mCaptcha ---------------------------------------------------------

  private loadMcaptchaConfig(): void {
    this.http.get<{ widgetUrl: string; sitekey: string }>('/auth/mcaptcha_config')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: cfg => this.mcaptchaConfig.set(cfg),
        error: err => {
          this.logger.error('failed to load mcaptcha config', err);
          this.error.set('Could not load captcha. Please refresh.');
        }
      });
  }

  private loadMcaptchaWidget(): void {
    const cfg = this.mcaptchaConfig();
    if (!cfg) return;

    const container = document.getElementById('mcaptcha-widget');
    if (!container) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${cfg.widgetUrl}?sitekey=${cfg.sitekey}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '95px';
    iframe.style.overflow = 'hidden';
    iframe.title = 'mCaptcha verification';
    iframe.setAttribute('scrolling', 'no');

    container.innerHTML = '';
    container.appendChild(iframe);
  }

  private handleMcaptchaMessage(event: MessageEvent): void {
    // Derive the expected origin from the configured widget URL (per-domain) so
    // the captcha host can differ between brands — distribution serves it from
    // mc.wolfpack-distribution.com, avoiding a cross-brand iframe on /r.
    const widgetUrl = this.mcaptchaConfig()?.widgetUrl;
    if (!widgetUrl) return;
    let expectedOrigin: string;
    try {
      expectedOrigin = new URL(widgetUrl).origin;
    } catch {
      return;
    }
    if (event.origin !== expectedOrigin) return;

    const data = event.data;
    let token: string | null = null;
    if (typeof data === 'string')        token = data;
    else if (data?.token)                token = data.token;
    else if (data?.m_captcha_token)      token = data.m_captcha_token;

    if (token) {
      this.captchaToken.set(token);
      this.captchaError.set(false);
    }
  }

  // ---- Submit -----------------------------------------------------------

  onSubmit(): void {
    if (this.form.invalid || !this.captchaToken()) {
      if (!this.captchaToken()) this.captchaError.set(true);
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.funnel.createLead({
      first_name:    this.form.value.first_name?.trim(),
      last_name:     this.form.value.last_name?.trim() || undefined,
      email:         this.form.value.email,
      sponsor_id:    this.sponsorId(),
      language:      this.i18n.getLanguage() || 'en',
      captcha_token: this.captchaToken()
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.loading.set(false);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'lead_submitted',
            name:     res.existing_account ? 'existing_account' : 'new_lead'
          });
          const lang = this.i18n.getLanguage() || 'en';
          // Always show the check-email screen so users aren't surprised by a
          // sudden redirect. The page reads `funnel.existing_account` from the
          // route state to swap copy when the email matched an existing card owner.
          this.router.navigate(
            ['/', lang, 'quiz', 'check-email'],
            { state: { existingAccount: !!res.existing_account } }
          );
        },
        error: err => {
          this.loading.set(false);
          this.captchaToken.set(null); // captcha is single-use — reset so user can retry
          this.error.set(err?.error?.error || 'Something went wrong. Please try again.');
        }
      });
  }
}
